import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom'
import ErrorPage from './pages/Error/error-page.jsx'
import BusinessEntity from './pages/BusinessEntity/BusinessEntity.jsx'
import ConfigTemplateList from './pages/ConfigTemplates/ConfigTemplateList.jsx'
import ConfigTemplateDetail from './pages/ConfigTemplates/ConfigTemplate.jsx'
import Dashboard from './pages/BusinessEntity/Dashboard.jsx'
import Devices from './pages/Devices/Devices.jsx'
import Forms from './pages/Forms/Forms.jsx'
import UserList from './pages/Security/UserList.jsx'
import UtgList from './pages/Utgs/UtgList.jsx'
import Utg from './pages/Utgs/Utg.jsx'
import UtgDetail from './pages/Utgs/UtgDetail.jsx'
import ApiInterfaceList from './pages/Utgs/ApiInterfaceList.jsx'
import ApiInterfaceDetail from './pages/Utgs/ApiInterfaceDetail.jsx'
import DeviceList from './pages/Utgs/DeviceList.jsx'
import DeviceDetail from './pages/Utgs/DeviceDetail.jsx'
import Config from './pages/Utgs/Config.jsx'
import InstallationList from './pages/Installations/InstallationList.jsx'
import PrivateRoute from './components/PrivateRoute.jsx'
import ProfileList from './pages/Profiles/ProfileList.jsx'
import Profile from './pages/Profiles/Profile.jsx'
import ProfileDetail from './pages/Profiles/ProfileDetail.jsx'
import ProfileApiInterfaceList from './pages/Profiles/ProfileApiInterfaceList.jsx'
import ProfileApiInterfaceDetail from './pages/Profiles/ProfileApiInterfaceDetail.jsx'
import ProfileDeviceList from './pages/Profiles/ProfileDeviceList.jsx'
import ProfileDeviceDetail from './pages/Profiles/ProfileDeviceDetail.jsx'
import ProfileConfig from './pages/Profiles/ProfileConfig.jsx'
import ProfileRawEdit from './pages/Profiles/ProfileRawEdit.jsx'
import TasksList from './pages/Tasks/TasksList.jsx'
import Home from './pages/Home/Home.jsx'

import './scss/styles.scss'
import './assets/favicon.ico'
import useScript from './hooks/useScript.js'
import { fetchAppConfig, fetchAccessCodeFromAuthCode, fetchUserByAccessToken, fetchUserPermissionMasks, fetchPermissions, fetchAccessTokenValidity } from './utils/api.js'
import { parseString } from './utils/utils.js'

let error

// The root loader handles getting the accessToken from SSO redirect and loads the user manifest as needed.
// This data is passed to our PrivateRoute component which then sets it as outlet context data for use in child routes
const rootLoader = async (request) => {
    let print = false
    // load application config data
    const config = await fetchAppConfig()
    const url = new URL(request.request.url)

    const authCode = url.searchParams.get('authcode')
    if (authCode) {
        // fetch and store accessToken if the user is being redirected from SSO
        const accessTokenResponse = await fetchAccessCodeFromAuthCode(config, authCode)
        const accessToken = accessTokenResponse.token
        if (print) {
            console.log('storing accessToken')
        }
        localStorage.setItem('accessToken', accessToken)
    }

    // load accessToken if its in local storage
    let storedToken = parseString(localStorage.getItem('accessToken'))
    if (!storedToken) {
        // user not logged in. Redirect to SSO or for local debugging, throw error to get redirect link so we can manually set accessToken in localStorage

        if (request.request.url.includes('localhost')) {
          error = new Error("Undefined access token")
          error.code = 401
          throw error
        }
        const url = `${config.ssoBaseUrl}?clientid=4&redirectToReferer=true`
        window.location.href = url
    }

    if (print) {
        console.log("Stored token is ".concat(storedToken.toString()).concat(" of type ").concat(typeof storedToken))
    }

    // validate accessToken
    const accessTokenObject = await fetchAccessTokenValidity(config, storedToken)
    if (!accessTokenObject) {
      // token is no longer valid
      localStorage.removeItem('accessToken')
      localStorage.removeItem('selectedBusinessEntityId')
      const url = `${config.ssoBaseUrl}?clientid=4&redirectToReferer=true`
      window.location.href = url
    }
    localStorage.setItem('accessToken', accessTokenObject.Token )
    storedToken = parseString(accessTokenObject.Token)

    // load user object from accessToken
    const user = await fetchUserByAccessToken(config, storedToken)

    // load permissions
    const permissions = await fetchPermissions(config, storedToken)

    // load user permission mask
    const permissionMasks = [...await fetchUserPermissionMasks(user.id, config, storedToken)].map(permission => {
        // remove some fields that aren't helpful
        delete permission.userId; // not needed since we have a user object
        delete permission.beList; // this is always null

        // augment with the actual permission name and key
        const p = permissions.find(element => element.id == permission.permissionId)
        if (p) {
            permission.name = p.name
            permission.key = p.key
        }
        // augment with flags rather than mask
        permission.canView = (16 & permission.levelMask) === 16
        permission.canEdit = (8 & permission.levelMask) === 8
        permission.canDelete = (4 & permission.levelMask) === 4
        permission.isEnabled = (2 & permission.levelMask) === 2
        permission.canGrant = (1 & permission.levelMask) === 1

        return permission
    })

    return {
        config: config,
        accessToken: storedToken,
        user: user,
        permissionMasks: permissionMasks,
        permissions: permissions,
        error: null
    }
}

const JsonFormLoader = () => {
    useScript('/jsonform/deps/jquery.min.js')
    useScript('/jsonform/deps/opt/jquery.ui.widget.js')
    useScript('/jsonform/deps/opt/jquery.ui.core.js')
    useScript('/jsonform/deps/opt/jquery.ui.mouse.js')
    useScript('/jsonform/deps/opt/jquery.ui.sortable.js')
    useScript('/jsonform/deps/opt/jquery-ui.js')
    useScript('/jsonform/deps/opt/jquery.transloadit2.js')
    useScript('/jsonform/deps/underscore.js')
    useScript('/jsonform/deps/opt/bootstrap-dropdown.js')
    useScript('/jsonform/deps/opt/jsv.js')
    useScript('/jsonform/lib/jsonform-defaults.js')
    useScript('/jsonform/lib/jsonform-split.js')
    useScript('/jsonform/lib/jsonform.js')
    useScript('/jsonform/lib/pubsub.js')
    useScript('/jsonform/lib/shift4-utils.js')
}

const router = createBrowserRouter(
  createRoutesFromElements(
      <Route loader={rootLoader} errorElement={<ErrorPage error={error}></ErrorPage>} element={<PrivateRoute />}>
      <Route path="/" element={<Home />} />
      <Route path="be/:beId" element={<BusinessEntity />} >
        <Route path="dashboard" element={<Dashboard />} handle='dashboard' />
        <Route path="utgs" element={<UtgList />} handle='utgs' />
        <Route path="utgs/:componentId" element={<Utg />} handle='utgs' >
          <Route path="" element={<UtgDetail />} />
          <Route path="apiinterfaces" element={<ApiInterfaceList />} />
          <Route path="apiinterfaces/:id" element={<ApiInterfaceDetail />} />
          <Route path="devices" element={<DeviceList />} />
          <Route path="devices/:name" element={<DeviceDetail />} />
          <Route path="config" element={<Config />} />
        </Route>
        <Route path="installations" element={<InstallationList />} handle='installations' />
        <Route path="profiles" element={<ProfileList />} handle='profiles' />
        <Route path="profiles/:profileId" element={<Profile />} handle='profiles'>
          <Route path="" element={<ProfileDetail />} />
          <Route path="apiinterfaces" element={<ProfileApiInterfaceList />} />
          <Route path="apiinterfaces/:id" element={<ProfileApiInterfaceDetail />} />
          <Route path="devices" element={<ProfileDeviceList />} />
          <Route path="devices/:name" element={<ProfileDeviceDetail />} />
          <Route path="config" element={<ProfileConfig />} />
          <Route path="edit" element={<ProfileRawEdit />} />
        </Route>
        <Route path="templates" element={<ConfigTemplateList />} handle='templates' />
        <Route path="templates/:configTemplateId" element={<ConfigTemplateDetail />} handle='templates' />
        <Route path="devices" element={<Devices />} handle="devices" />
        <Route path="forms" element={<Forms />} handle="forms" />
        <Route path="tasks" element={<TasksList />} handle='tasks' />
        <Route path="users" element={<UserList />} handle='users' />
      </Route>
    </Route>
  )
)
ReactDOM.createRoot(document.getElementById('root')).render(
  <>
    <JsonFormLoader></JsonFormLoader>
    <RouterProvider router={router} />
  </>
)